"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageEcommerceSchema } from "@/sanity/pages/PageEcommerceTemplate";
import Image from "@/helpers/Image";
import dots from "@/public/ecommerce/dots.png";

function XIcon() {
  return (
    <div className="w-[4rem] h-[4rem] shrink-0 bg-[#BF0505] rounded-[0.25rem] flex items-center justify-center">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          d="M2 2L10 10M10 2L2 10"
          stroke="white"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function CheckIcon() {
  return (
    <div className="w-[4rem] h-[4rem] shrink-0 bg-[#D6EEE7] rounded-[0.25rem] flex items-center justify-center">
      <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
        <path
          d="M1.5 5.5L5.5 9.5L12.5 1.5"
          stroke="#003828"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function ComparisonSection({
  data,
}: {
  data: PageEcommerceSchema;
}) {
  if (
    !data.comparisonTitle &&
    !data.comparisonLeftLabel &&
    !data.comparisonRightLabel
  )
    return null;

  return (
    <section className="w-full bg-[#f7f7f7] py-[7rem] lg:py-[16rem]">
      <div className="mx-auto w-[min(170rem,100%)] px-[4rem] flex flex-col items-center gap-[6rem] lg:gap-[8rem]">
        {/* Header */}
        <div className="flex flex-col items-center gap-[2rem] text-center max-w-[120rem]">
          {data.comparisonChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.comparisonChip}
            </p>
          )}
          {data.comparisonTitle && (
            <div className="richtext richtext-center prose-headings:font-medium prose-headings:text-[5rem] lg:prose-headings:text-[6rem] prose-headings:leading-[1.15] prose-headings:mb-[2rem] prose-headings:tracking-[-0.3rem] lg:max-w-[100.375rem]">
              <PortableText
                value={data.comparisonTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row gap-[2rem] w-full max-w-[130rem]">
          {/* Left card — own account */}
          <div className="flex-1 bg-white border border-black/10 rounded-[0.25rem] p-[4rem] lg:px-[8rem] lg:py-[6rem] flex flex-col gap-[3rem]">
            {data.comparisonLeftLabel && (
              <span className="font-geist font-semibold text-[2.25rem] uppercase text-black leading-[1.33]">
                {data.comparisonLeftLabel}
              </span>
            )}
            <ul className="flex flex-col">
              {data.comparisonLeftItems?.map((item, i) => (
                <li key={item._key ?? i} className="flex flex-col gap-0">
                  <div className="flex items-center gap-[1.375rem] py-[2.25rem]">
                    <XIcon />
                    <span className="text-[2rem] font-medium text-black/[0.62] leading-normal">
                      {item.text}
                    </span>
                  </div>
                  {i < (data.comparisonLeftItems?.length ?? 0) - 1 && (
                    <div className="h-px bg-black/10" />
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Right card — IQ challenge */}
          <div
            className="flex-1 rounded-[0.25rem] p-[4rem] lg:px-[8rem] lg:py-[6rem] flex flex-col gap-[3rem] overflow-hidden relative"
            style={{ background: "#0d2922" }}
          >
            <Image
              src={dots}
              alt="dots"
              className="absolute inset-0 !w-full !h-full object-cover opacity-70 mix-blend-luminosity"
            />
            {data.comparisonRightLabel && (
              <span className="relative z-10 font-geist font-semibold text-[2.25rem] uppercase text-[#d6eee7] leading-[1.33]">
                {data.comparisonRightLabel}
              </span>
            )}
            <ul className="relative z-10 flex flex-col">
              {data.comparisonRightItems?.map((item, i) => (
                <li key={item._key ?? i} className="flex flex-col gap-0">
                  <div className="flex items-center gap-[1.375rem] py-[2.25rem]">
                    <CheckIcon />
                    <span className="text-[2rem] font-medium text-white/[0.62] leading-normal">
                      {item.text}
                    </span>
                  </div>
                  {i < (data.comparisonRightItems?.length ?? 0) - 1 && (
                    <div className="h-px bg-white/10" />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
